import { useEffect, useRef, useState } from "react";
import { Eraser, PenLine } from "lucide-react";

/**
 * A lightweight signature pad. Draws with mouse or finger on an HTML
 * canvas and reports the result as a PNG data URL via onChange. Calls
 * onChange(null) when cleared. No external library.
 */
export function SignaturePad({ onChange, disabled = false, testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#131822";
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk && onChange) onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    if (onChange) onChange(null);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="k-quiet !text-xs flex items-center gap-1.5">
          <PenLine size={12} /> draw your signature
        </span>
        <button
          type="button"
          onClick={clear}
          disabled={disabled || !hasInk}
          className="k-btn k-btn-ghost !py-1 !px-2 !text-xs disabled:opacity-40"
          data-testid={`${testid}-clear`}
        >
          <Eraser size={12} /> clear
        </button>
      </div>
      <div
        className="relative rounded-lg overflow-hidden"
        style={{ border: "1px solid hsl(var(--klareet-line))", background: "#fff" }}
      >
        <div
          className="absolute left-4 right-4 pointer-events-none"
          style={{ bottom: 26, borderBottom: "1px dashed hsl(var(--klareet-line))" }}
        />
        {!hasInk && (
          <span
            className="absolute left-4 pointer-events-none klareet-serif"
            style={{ bottom: 6, color: "hsl(var(--klareet-muted))", fontSize: "0.85rem", fontStyle: "italic" }}
          >
            sign here
          </span>
        )}
        <canvas
          ref={canvasRef}
          className="w-full block touch-none cursor-crosshair"
          style={{ height: 128 }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={`${testid}-canvas`}
        />
      </div>
    </div>
  );
}

export default SignaturePad;
